import React, { useMemo } from 'react';
import { Card, Table, Typography, Tag } from 'antd';
import moment from 'moment';

const { Text } = Typography;

/**
 * Action Log table for a spray job. Renders every audit-trail row the
 * backend persists via logSprayJobChange — Prepare / Initial Fill /
 * Zone start / Zone finish / Refill / Pause / Cleanup / Complete — in
 * one chronological table with the action, timestamp, and the user who
 * performed it.
 *
 * Stakeholder (Damien, round-5 follow-up): "the action log should be a
 * table of rows, document the action taken, the timestamp of when it
 * was done, and who made the action."
 *
 * Source data: change_log array on the spray-job detail response (the
 * sprayService.getChangeLog endpoint). Each entry has action, field,
 * user_id, user_name (resolved server-side), metadata (JSON), and
 * created_at.
 */

// Human-readable label per (action, field) pair. Falls back to a
// title-cased version of the action when no specific mapping exists,
// so legacy log rows still render something readable.
const ACTION_LABELS = {
  // job lifecycle
  'create::job': 'Spray job created',
  'update::job_data': 'Spray job updated',
  // status changes are labelled by their `to` value in metadata; see
  // formatLabel below for the dynamic path.
  // round-4/5/6 explicit per-action events
  'zone_start::zone': 'Zone started',
  'zone_resume::zone': 'Zone resumed',
  'zone_finish::zone': 'Zone finished',
  'refill_logged::refill': 'Refill logged',
  'initial_fill_logged::initial_fill': 'Initial fill logged',
  'ppe_check_logged::ppe': 'PPE check confirmed',
  'task_logged::task': 'Task confirmed',
  'unplanned_refill_inserted::refill': 'Unplanned refill',
};

const STATUS_LABELS = {
  to_be_started: 'To Be Started',
  prepared: 'Prepared',
  spraying: 'Spraying',
  spray_cleanup: 'Spray Cleanup',
  waiting_for_completion: 'Waiting for Completion',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const formatLabel = (row) => {
  const key = `${row.action}::${row.field}`;
  if (ACTION_LABELS[key]) return ACTION_LABELS[key];
  if (row.action === 'status_change') {
    const m = row.metadata || {};
    // The pause flow flips status back to PREPARED with action='stop_spraying'
    // metadata — render that as a more descriptive "Zone paused" rather
    // than the generic "Status → Prepared" so the action log reads
    // naturally for operators.
    if (m.action === 'stop_spraying') {
      return 'Zone paused';
    }
    const to = m.to;
    const label = STATUS_LABELS[to] || to || '—';
    return `Status → ${label}`;
  }
  // Fallback: title-case the action so unknown rows still read OK.
  return String(row.action || '—')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Format a zone-coordinate object into the same "K · B1 · R100" shape
 * used elsewhere in the spray module. Accepts both the new `position`
 * shape ({letter, bench, row}) and the legacy pause_zone shape
 * ({letter, bench, row, notes}).
 */
const formatPosition = (p) => {
  if (!p || typeof p !== 'object') return null;
  const parts = [
    p.letter,
    p.bench != null && p.bench !== '' ? `B${p.bench}` : null,
    p.row != null && p.row !== '' ? `R${p.row}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

const formatDetail = (row) => {
  const m = row.metadata || {};
  // Prefer the server-computed zone_label when present (round-6) —
  // counts non-refill items so "Zone 2" is the operator's Zone 2,
  // not the heterogeneous list index.
  const zoneLabel = m.zone_label || (m.zone_index != null ? `Zone ${m.zone_index + 1}` : null);
  if (row.action === 'zone_start' || row.action === 'zone_resume' || row.action === 'zone_finish') {
    const parts = [zoneLabel];
    // Coordinates the operator was at: zone's planned start for
    // start/resume rows, zone's planned finish for finish rows.
    // Captured server-side from the zone item in the zones JSON.
    const pos = formatPosition(m.position);
    if (pos) parts.push(pos);
    // Full weather snapshot captured at start/resume — temperature
    // and humidity persisted alongside the Stull-derived delta_t so
    // the audit row shows the exact conditions the operator started
    // in, and the post-completion "actual environmental range" can
    // aggregate min/max across these entries.
    if (m.temperature != null) parts.push(`${m.temperature}°C`);
    if (m.humidity != null) parts.push(`${m.humidity}%`);
    if (m.delta_t != null) parts.push(`Delta T ${m.delta_t}`);
    return parts.filter(Boolean).join(' · ') || null;
  }
  if (row.action === 'refill_logged') {
    const liters = m.liters != null ? `${m.liters} L` : '';
    const after = m.refill_index != null ? `refill #${m.refill_index + 1}` : '';
    return [liters, after].filter(Boolean).join(' · ');
  }
  if (row.action === 'unplanned_refill_inserted') {
    // Operator hit Pause → picked "Unplanned refill" → posted total
    // volume. Backend injected a refill marker BEFORE the active zone
    // and logged this row. Detail shows the zone they were on, where
    // in the field they stopped, and the litres they're loading.
    const parts = [];
    if (zoneLabel) parts.push(zoneLabel);
    if (m.liters != null) parts.push(`${m.liters} L`);
    const pos = formatPosition(m.pause_zone);
    if (pos) parts.push(pos);
    return parts.filter(Boolean).join(' · ') || null;
  }
  if (row.action === 'ppe_check_logged') {
    return m.zone_label ? `before ${m.zone_label}` : null;
  }
  if (row.action === 'task_logged') {
    return m.task_name || null;
  }
  if (row.action === 'status_change') {
    if (m.action === 'stop_spraying') {
      // Pause coords are operator-entered via the PauseZoneModal —
      // include them on the row alongside the zone label so the audit
      // trail shows precisely where in the field the spray was paused.
      const parts = [zoneLabel];
      const pos = formatPosition(m.pause_zone);
      if (pos) parts.push(pos);
      return parts.filter(Boolean).join(' · ') || null;
    }
    if (m.action === 'refill_complete' && m.continued_as_job_id) {
      return `Continued as job #${m.continued_as_job_id}`;
    }
    return null;
  }
  return null;
};

const SprayJobActionLog = ({ changeLog }) => {
  // Backend returns logs in DESC order; flip to chronological so the
  // operator reads the action history top-to-bottom in run order.
  const rows = useMemo(() => {
    if (!Array.isArray(changeLog)) return [];
    return changeLog
      .slice()
      .reverse()
      .map((r) => ({
        key: r.id,
        action: r.action,
        field: r.field,
        label: formatLabel(r),
        detail: formatDetail(r),
        user: r.user_name || (r.user_id ? `User #${r.user_id}` : '—'),
        when: r.created_at,
      }));
  }, [changeLog]);

  const columns = [
    {
      title: 'Action',
      dataIndex: 'label',
      key: 'label',
      render: (label, row) => (
        <span>
          <Text strong>{label}</Text>
          {row.detail && (
            <Tag style={{ marginLeft: 8 }}>{row.detail}</Tag>
          )}
        </span>
      ),
    },
    {
      title: 'When',
      dataIndex: 'when',
      key: 'when',
      width: 220,
      render: (v) => {
        if (!v) return '—';
        const m = moment(v);
        return m.isValid()
          ? <span>{m.format('YYYY-MM-DD HH:mm:ss')}</span>
          : <span>{v}</span>;
      },
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 180,
    },
  ];

  return (
    <Card title="Action Log" style={{ marginBottom: 16 }}>
      <Table
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 20 ? { pageSize: 20 } : false}
        locale={{ emptyText: 'No actions logged yet.' }}
      />
    </Card>
  );
};

export default SprayJobActionLog;
